import { useCallback, useEffect, useMemo, useState } from 'react';
import cn from 'clsx';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showNotification, captureException } from 'r/utils';
import {
  fetchApiRaw,
  DELETE_ANALYSES_CMS,
  DELETE_ANALYSES,
  DELETE_MARKET_SCORE,
} from 'r/utils/fetchApiRaw';
import { StoredAuth } from 'r/utils/Stored';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import type { Analyse, ScoreMarket } from 'r/types';
import { isMarketStarted } from 'r/utils/scoreMarket';

const API_AUTHOR_ID = '485b4d0f-575b-48c5-b02f-f18c8e57dba7';

interface AnalysisInfo {
  sanitizerAnalysis: Analyse | undefined;
  existingMarket: ScoreMarket | undefined;
  isBlockedByMarket: boolean;
  isInSanitizer: boolean;
  hasMarket: boolean;
}

interface ModalBulkDeleteProps {
  analyses: Analyse[];
  getSanitizerAnalysis: (analysis: Analyse) => Analyse | undefined;
  getExistingMarket: (analysis: Analyse) => ScoreMarket | undefined;
  onSuccess: () => void;
}

export default function ModalBulkDelete({
  analyses,
  getSanitizerAnalysis,
  getExistingMarket,
  onSuccess,
}: ModalBulkDeleteProps) {
  const [isOpen, setIsOpen] = useState(true);
  const queryClient = useQueryClient();

  // Pre-compute sanitizer/market info for each analysis
  const analysisInfoMap = useMemo(() => {
    const map = new Map<string, AnalysisInfo>();
    analyses.forEach((a) => {
      const sanitizerAnalysis = getSanitizerAnalysis(a);
      const existingMarket = getExistingMarket(a);
      const isBlockedByMarket =
        !!existingMarket && isMarketStarted(existingMarket);
      map.set(a.id, {
        sanitizerAnalysis,
        existingMarket,
        isBlockedByMarket,
        isInSanitizer: !!sanitizerAnalysis,
        hasMarket: !!existingMarket,
      });
    });
    return map;
  }, [analyses, getSanitizerAnalysis, getExistingMarket]);

  const [selected, setSelected] = useState<Analyse[]>(() =>
    analyses.filter((a) => !analysisInfoMap.get(a.id)?.isBlockedByMarket),
  );

  useEffect(() => {
    setSelected(
      analyses.filter((a) => !analysisInfoMap.get(a.id)?.isBlockedByMarket),
    );
  }, [analyses, analysisInfoMap]);

  const handleToggle = useCallback(
    (analysis: Analyse) => {
      if (analysisInfoMap.get(analysis.id)?.isBlockedByMarket) return;
      setSelected((prev) =>
        prev.some((a) => a.id === analysis.id) ?
          prev.filter((a) => a.id !== analysis.id)
        : [...prev, analysis],
      );
    },
    [analysisInfoMap],
  );

  const blockedAnalyses = useMemo(
    () => analyses.filter((a) => analysisInfoMap.get(a.id)?.isBlockedByMarket),
    [analyses, analysisInfoMap],
  );

  const { mutateAsync: submitDelete, isPending: isSubmitting } = useMutation({
    mutationFn: async () => {
      const accessTokens = StoredAuth.getAll();
      await Promise.all(
        selected.map(async (a) => {
          const info = analysisInfoMap.get(a.id);
          if (!info || info.isBlockedByMarket) return;

          // 1. Delete from sanitizer first
          if (info.sanitizerAnalysis) {
            await fetchApiRaw(
              DELETE_ANALYSES,
              { urlParams: [info.sanitizerAnalysis.id] },
              accessTokens.default,
            );
          }
          // 2. Delete the Score market (if applicable and not started)
          if (info.existingMarket) {
            await fetchApiRaw(
              DELETE_MARKET_SCORE,
              { urlParams: [info.existingMarket.id] },
              accessTokens.cms,
            );
          }
          // 3. Delete from CMS
          await fetchApiRaw(
            DELETE_ANALYSES_CMS,
            { urlParams: [a.id] },
            accessTokens.cms,
          );
        }),
      );
    },
    onSuccess: () => {
      const hasSanitizer = selected.some(
        (a) => analysisInfoMap.get(a.id)?.isInSanitizer,
      );
      const hasMarkets = selected.some(
        (a) => analysisInfoMap.get(a.id)?.hasMarket,
      );
      showNotification(
        'success',
        `Deleted ${selected.length} analysis${selected.length > 1 ? 'es' : ''}${hasSanitizer ? ' (and sanitizer entries)' : ''}${hasMarkets ? ' (and Score markets)' : ''}`,
      );
      queryClient.invalidateQueries({ queryKey: ['cms-analyses'] });
      if (hasSanitizer) {
        queryClient.invalidateQueries({
          queryKey: ['play-analyses-sanitizer'],
        });
      }
      if (hasMarkets) {
        queryClient.invalidateQueries({ queryKey: ['score-markets'] });
      }
      setIsOpen(false);
      onSuccess();
    },
    onError: (error) => {
      captureException(error);
      showNotification('error', 'Failed to delete analyses');
    },
  });

  const handleSubmit = async () => {
    try {
      await submitDelete();
    } catch {
      // error handled by mutation's onError, dialog stays open for retry
    }
  };

  // Exclusion filters (from currently selected analyses)
  const excludeVerifiedAnalyses = analyses.filter(
    (a) => selected.some((s) => s.id === a.id) && a.verifiedBy,
  );
  const excludeApiAnalyses = analyses.filter(
    (a) =>
      selected.some((s) => s.id === a.id) &&
      a.analysisAuthorId === API_AUTHOR_ID,
  );
  const excludeDeletedFromTweeter = analyses.filter(
    (a) =>
      selected.some((s) => s.id === a.id) && a.deletedFromTweeterAt !== null,
  );

  const remainingCount =
    selected.length -
    excludeVerifiedAnalyses.length -
    excludeApiAnalyses.length -
    excludeDeletedFromTweeter.length;

  const handleClose = () => {
    setIsOpen(false);
    onSuccess();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Confirm {analyses.length > 1 && 'Bulk '}Deletion
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Blocked analyses warning */}
          {blockedAnalyses.length > 0 && (
            <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <p className="text-sm text-yellow-700">
                {blockedAnalyses.length} analysis
                {blockedAnalyses.length > 1 ? 'es' : ''} cannot be deleted —
                linked Score market
                {blockedAnalyses.length > 1 ? 's are' : ' is'} already live.
              </p>
            </div>
          )}

          <div className="max-h-[40vh] overflow-y-auto space-y-3">
            {analyses.map((analysis) => {
              const isSelected = selected.some((a) => a.id === analysis.id);
              const info = analysisInfoMap.get(analysis.id);
              const isBlocked = info?.isBlockedByMarket ?? false;

              return (
                <div
                  key={analysis.id}
                  className={cn(
                    'flex items-start gap-3 p-3 border rounded-lg transition-colors',
                    isBlocked ?
                      'border-red-500/40 bg-red-500/5'
                    : 'border-(--textTertiary)',
                    !isBlocked && isSelected && 'bg-card',
                  )}
                >
                  <Checkbox
                    checked={isSelected && !isBlocked}
                    onCheckedChange={() => handleToggle(analysis)}
                    disabled={isBlocked}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-semibold text-main">
                        {analysis.authorName}
                      </span>
                      <span className="text-xs text-secondary">
                        @{analysis.authorUsername}
                      </span>
                      {analysis.verifiedBy && (
                        <Badge variant="secondary">Verified</Badge>
                      )}
                      {analysis.deletedFromTweeterAt !== null && (
                        <Badge variant="destructive">Deleted</Badge>
                      )}
                      {analysis.analysisAuthorId === API_AUTHOR_ID && (
                        <Badge variant="outline">API</Badge>
                      )}
                      {info?.isInSanitizer && (
                        <Badge className="bg-green-100 text-green-800">
                          Sanitizer
                        </Badge>
                      )}
                      {info?.hasMarket && !isBlocked && (
                        <Badge variant="secondary">Score Market</Badge>
                      )}
                      {isBlocked && (
                        <Badge variant="destructive">Market Live</Badge>
                      )}
                    </div>

                    {isBlocked && (
                      <p className="text-xs text-red-500 mb-1">
                        Cannot delete — Score market is already live
                      </p>
                    )}
                    {!isBlocked && info?.hasMarket && (
                      <p className="text-xs text-yellow-600 mb-1">
                        Linked Score market will also be deleted
                      </p>
                    )}
                    {!isBlocked && info?.isInSanitizer && (
                      <p className="text-xs text-green-700 mb-1">
                        Will also be removed from Sanitizer
                      </p>
                    )}

                    <p className="text-sm text-main line-clamp-2">
                      {analysis.tweetText}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <Separator />

          {/* Exclusion Options */}
          <div className="space-y-3">
            <span className="text-sm font-semibold text-main">
              Exclude from deletion
            </span>
            <div className="flex items-center space-x-2">
              <Checkbox
                checked={excludeVerifiedAnalyses.length > 0}
                onCheckedChange={() => {
                  setSelected(analyses.filter((a) => a.verifiedBy));
                }}
              />
              <label className="text-sm">
                Verified analyses ({excludeVerifiedAnalyses.length})
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                checked={excludeApiAnalyses.length > 0}
                onCheckedChange={() => {
                  setSelected(
                    analyses.filter(
                      (a) => a.analysisAuthorId !== API_AUTHOR_ID,
                    ),
                  );
                }}
              />
              <label className="text-sm">
                API analyses ({excludeApiAnalyses.length})
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                checked={excludeDeletedFromTweeter.length > 0}
                onCheckedChange={() => {
                  setSelected(
                    analyses.filter((a) => a.deletedFromTweeterAt === null),
                  );
                }}
              />
              <label className="text-sm">
                Deleted from Twitter ({excludeDeletedFromTweeter.length})
              </label>
            </div>

            {remainingCount > 0 && (
              <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                <p className="text-sm text-green-700">
                  Analyses to be deleted: {remainingCount}
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleSubmit}
              disabled={selected.length === 0 || isSubmitting}
            >
              {isSubmitting ? 'Deleting...' : 'Confirm Delete'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
